// Reference:
// * https://github.com/epicweb-dev/epic-stack/blob/ae3f66a5296a3ed3bcf6ffd51187c4d1249dbcc3/app/utils/client-hints.tsx

// This file contains utilities for using client hints for user preference
// which are needed by the server, but are only known by the browser.
// * https://github.com/epicweb-dev/client-hints
// * https://github.com/epicweb-dev/epic-stack/blob/a69e96383a3fd1775c58f34c33a54a9fd4aa0502/docs/decisions/005-client-pref-cookies.md

import { getHintUtils } from "@epic-web/client-hints";
import {
  clientHint as colorSchemeHint,
  subscribeToSchemeChange,
} from "@epic-web/client-hints/color-scheme";
import { clientHint as timeZoneHint } from "@epic-web/client-hints/time-zone";
import { useEffect } from "react";
import { useRevalidator } from "react-router";

import { useRequestInfo } from "@/utils/request-info";

const hintsUtils = getHintUtils({
  theme: colorSchemeHint,
  timeZone: timeZoneHint,

  // Add other client hints here if needed.
});

// This is typically called in the loader to pass client hints to the request
// (in requestInfo).
//
export const { getHints } = hintsUtils;

// Get the client hints and their values from the request (in requestInfo).
//
export function useHints() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const requestInfo = useRequestInfo();

  /* eslint-disable-next-line @typescript-eslint/no-unsafe-return,
                              @typescript-eslint/no-unsafe-member-access
  */
  return requestInfo.hints;
}

// Inject an inline script element that checks for client hints and sets
// cookies if they are not set, then reloads the page if any cookie was set to
// an inaccurate value (i.e., any cookie changes).
//
export function ClientHintCheck({ nonce }: { nonce: string }) {
  // Used to trigger a revalidation of the page. Revalidation allows the page
  // to be updated with fresh data or updated states without requiring a full
  // page reload.
  //
  const { revalidate } = useRevalidator();

  useEffect(
    () => {
      // Subscribe to changes in the user's color scheme preference.
      //
      // Internally, this uses window.matchMedia with the prefers-color-scheme
      // media feature to detect whether the user prefers dark or light mode,
      // and updates the cookie accordingly.
      // * https://github.com/epicweb-dev/client-hints/blob/39ef16d8acbf8b5ab78abd1480aa96584706f357/src/color-scheme.ts
      //
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      const unsubscribe: () => void = subscribeToSchemeChange(() =>
        revalidate(),
      );

      // Clean up the subscription when the component unmounts.
      //
      return () => unsubscribe();
    },

    // Dependency array: revalidate function must be stable to avoid
    // unnecessary re-subscriptions.
    //
    [revalidate],
  );

  return (
    <script
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: hintsUtils.getClientHintCheckScript(),
      }}
    />
  );
}
